import {Color} from '@benchling/nucleus-ui';
import {createStyles} from '@mantine/core';
import React from 'react';

import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface ContextChipsProps {
  contextIds: ReadonlyArray<string>;
  /** Removes a context item. When omitted, chips render without a remove control. */
  onRemove?: (id: string) => void;
}

/**
 * Chips shown above the composer for items the chat references, applied as context on first send.
 * Separate from TargetItemChip, which marks the item the agent may write to; context grants no write scope.
 */
export default function ContextChips({contextIds, onRemove}: ContextChipsProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div className={classes.outer}>
      {contextIds.map((id) => (
        <div key={id} className={classes.chipRow}>
          <div className={classes.chipGroup}>
            <BenchlingIcon className={classes.icon} icon="file" />
            <BenchChip
              className={classes.chip}
              size={ChipSize.MINI}
              id={id}
              onRemove={
                onRemove
                  ? () => {
                      onRemove(id);
                    }
                  : undefined
              }
            />
          </div>
          <BenchlingIcon className={classes.icon} icon="chevron-right" aria-hidden={true} />
        </div>
      ))}
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  outer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.lg} ${theme.spacing.lg} 0`,
  },
  chipRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    minWidth: 0,
  },
  chipGroup: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: Color.BLUE_50,
    borderRadius: theme.radius.sm,
    padding: theme.spacing.xs,
    minWidth: 0,
    overflow: 'hidden',
  },
  icon: {
    color: Color.BLUE_450,
    fontSize: '12px',
    flexShrink: 0,
  },
  chip: {
    fontSize: '13px',
    lineHeight: '12px',
    whiteSpace: 'nowrap',
    margin: 0,
    padding: 0,
    minWidth: 0,
    minHeight: 0,
    height: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    // The surrounding box provides the background.
    backgroundColor: 'transparent !important',
    border: 'none !important',
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
    // A file icon is rendered alongside the chip, so suppress the built-in type icon.
    ' .chip-icon': {
      display: 'none',
    },
    ' .chip-text': {
      color: `${Color.GRAY_900} !important`,
      fontWeight: 400,
    },
    ' .chip-text-secondaryText': {
      color: `${Color.BLUE_450} !important`,
    },
    ' .chip-button': {
      color: `${Color.BLUE_450} !important`,
      fontSize: '8px !important',
      marginLeft: `${theme.spacing.xs} !important`,
    },
    ' a': {
      color: `${Color.GRAY_900} !important`,
    },
  },
}));
